import { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faChartBar,
  faChartLine,
  faChartArea,
  faChartPie,
  faBullseye,
  faTable,
  faBolt,
  faFire,
  faFilter,
  faTachometerAlt,
  faProjectDiagram,
  faWater,
  faChartColumn,
  faTh,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import type { WidgetType } from '../../../types/bi';
import styles from './WidgetToolbar.module.css';

const WIDGET_CATALOG: {
  type: WidgetType;
  label: string;
  icon: typeof faChartBar;
  description: string;
}[] = [
  {
    type: 'bar',
    label: 'Bar Chart',
    icon: faChartBar,
    description: 'Compare values across categories',
  },
  { type: 'line', label: 'Line Chart', icon: faChartLine, description: 'Trends over time' },
  { type: 'area', label: 'Area Chart', icon: faChartArea, description: 'Filled trends over time' },
  { type: 'pie', label: 'Pie Chart', icon: faChartPie, description: 'Part-to-whole breakdown' },
  {
    type: 'donut',
    label: 'Donut Chart',
    icon: faBullseye,
    description: 'Part-to-whole with centre label',
  },
  {
    type: 'kpi',
    label: 'KPI Card',
    icon: faTachometerAlt,
    description: 'Single metric with delta',
  },
  { type: 'sparkline', label: 'Sparkline', icon: faBolt, description: 'Compact inline trend' },
  {
    type: 'data_table',
    label: 'Data Table',
    icon: faTable,
    description: 'Tabular data with pagination',
  },
  { type: 'scatter', label: 'Scatter Plot', icon: faTh, description: 'X-Y correlation' },
  { type: 'heatmap', label: 'Heat Map', icon: faFire, description: 'Density matrix' },
  { type: 'funnel', label: 'Funnel', icon: faFilter, description: 'Stage conversion' },
  { type: 'gauge', label: 'Gauge', icon: faTachometerAlt, description: 'Progress dial' },
  { type: 'treemap', label: 'Treemap', icon: faProjectDiagram, description: 'Hierarchical area' },
  { type: 'waterfall', label: 'Waterfall', icon: faWater, description: 'Incremental totals' },
  {
    type: 'pivot_table',
    label: 'Pivot Table',
    icon: faChartColumn,
    description: 'Cross-tab grouping',
  },
];

const AVAILABLE_TYPES = new Set<WidgetType>([
  'bar',
  'line',
  'area',
  'pie',
  'donut',
  'kpi',
  'sparkline',
  'data_table',
  'scatter',
  'heatmap',
  'funnel',
  'gauge',
  'treemap',
  'waterfall',
  'pivot_table',
]);

interface WidgetToolbarProps {
  onAddWidget: (type: WidgetType) => void;
}

export default function WidgetToolbar({ onAddWidget }: WidgetToolbarProps) {
  const [open, setOpen] = useState(false);

  const handleSelect = useCallback(
    (type: WidgetType) => {
      if (!AVAILABLE_TYPES.has(type)) return;
      onAddWidget(type);
      setOpen(false);
    },
    [onAddWidget],
  );

  return (
    <div className={styles.toolbar}>
      <button className={styles.addBtn} onClick={() => setOpen(true)}>
        <FontAwesomeIcon icon={faPlus} /> Add Widget
      </button>

      {open && (
        <div className={styles.overlay} onClick={() => setOpen(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Choose a widget type</h3>
              <button className={styles.closeBtn} onClick={() => setOpen(false)}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className={styles.grid}>
              {WIDGET_CATALOG.map((w) => {
                const available = AVAILABLE_TYPES.has(w.type);
                return (
                  <button
                    key={w.type}
                    className={`${styles.card} ${!available ? styles.disabled : ''}`}
                    onClick={() => handleSelect(w.type)}
                    disabled={!available}
                    title={!available ? 'Coming soon' : w.description}
                  >
                    <FontAwesomeIcon icon={w.icon} className={styles.cardIcon} />
                    <span className={styles.cardLabel}>{w.label}</span>
                    {!available && <span className={styles.badge}>Soon</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
